import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { getProspects, importCsv, analyzeProspect, exportPipelineCsv, getProspect } from "../lib/api";
import { PriorityList } from "../components/PriorityList";
import { DatabaseFilters, applyFilters } from "../components/database/DatabaseFilters";
import { ProspectTable } from "../components/database/ProspectTable";
import { BulkIgButton, BulkIgErrors, useBulkIgJob } from "../components/database/BulkIgFetch";
import { Button } from "../components/ui/button";
import { Plus, Upload, Download, Target, List } from "lucide-react";
import { toast } from "sonner";

const INITIAL_FILTERS = { q: "", sector: "all", priority: "all", status: "all", sortDesc: true };

export default function Database({ onOpenProspect, onNew, refreshKey }) {
  const [prospects, setProspects] = useState([]);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [importing, setImporting] = useState(false);
  const [view, setView] = useState("all");
  const fileRef = useRef();

  const load = useCallback(() => getProspects().then(setProspects).catch((e) => console.warn("prospects load failed", e)), []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const bulk = useBulkIgJob(load);
  const setFilter = useCallback((k, v) => setFilters((f) => ({ ...f, [k]: v })), []);
  const toggleSort = useCallback(() => setFilters((f) => ({ ...f, sortDesc: !f.sortDesc })), []);
  const filtered = useMemo(() => applyFilters(prospects, filters), [prospects, filters]);

  const openById = async (id) => {
    const p = prospects.find((x) => x.id === id) || await getProspect(id);
    onOpenProspect(p);
  };

  const doImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const res = await importCsv(file);
      toast.success(`${res.inserted} prospect importati`);
      load();
    } catch {
      toast.error("Import fallito. Verifica il formato CSV.");
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const doExport = async () => {
    try {
      const blob = await exportPipelineCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "pipeline_20-80lab.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Pipeline esportata in CSV");
    } catch { toast.error("Export CSV fallito"); }
  };

  const quickAnalyze = async (e, id) => {
    e.stopPropagation();
    toast.info("Analisi AI avviata...");
    try {
      const res = await analyzeProspect(id);
      setProspects((prev) => prev.map((p) => (p.id === id ? res : p)));
      toast.success("Analisi completata 🔥");
    } catch {
      toast.error("Analisi fallita");
    }
  };

  return (
    <div className="fade-in space-y-6 p-6 md:p-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-caps text-primary">01 · Find & Score</div>
          <h1 className="mt-1 font-heading text-4xl font-bold tracking-tight">Prospect Database</h1>
          <p className="mt-1 text-sm text-muted-foreground">{filtered.length} di {prospects.length} prospect</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={doImport} data-testid="csv-input" />
          <BulkIgButton job={bulk.job} onStart={bulk.start} />
          <Button variant="outline" onClick={doExport} data-testid="export-csv-btn">
            <Download className="mr-2 h-4 w-4" /> Esporta CSV
          </Button>
          <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={importing} data-testid="import-csv-btn">
            <Upload className="mr-2 h-4 w-4" /> {importing ? "Import..." : "Importa CSV"}
          </Button>
          <Button onClick={onNew} data-testid="new-prospect-btn">
            <Plus className="mr-2 h-4 w-4" /> Nuovo Prospect
          </Button>
        </div>
      </div>

      <BulkIgErrors job={bulk.job} onClose={bulk.clear} />
      <ViewToggle view={view} onChange={setView} />

      {view === "priority" && <PriorityList onOpen={openById} refreshKey={`${refreshKey}-${prospects.length}`} />}
      {view === "all" && (
        <>
          <DatabaseFilters filters={filters} onChange={setFilter} />
          <ProspectTable rows={filtered} onOpen={onOpenProspect} onToggleSort={toggleSort} onQuickAnalyze={quickAnalyze} />
        </>
      )}
    </div>
  );
}

function ViewToggle({ view, onChange }) {
  const cls = (v) => `flex items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors ${view === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`;
  return (
    <div className="flex gap-1 rounded-lg border border-border bg-card p-1 w-fit" data-testid="view-toggle">
      <button onClick={() => onChange("all")} data-testid="view-all-btn" className={cls("all")}><List className="h-4 w-4" /> Tutti i prospect</button>
      <button onClick={() => onChange("priority")} data-testid="view-priority-btn" className={cls("priority")}><Target className="h-4 w-4" /> Da contattare ora</button>
    </div>
  );
}
